<template>
  <div
    :data-testid="dataTestid"
    class="grid md:grid-cols-12 grid-cols-10 gap-y-2 mt-2"
  >
    <!--
      Flex rather than grid columns: the label used to sit in a single 1/12 column, narrower
      than "Strong" itself, so the right-aligned text spilled back over the bar. The label now
      keeps its own width and the bar takes whatever is left.
    -->
    <div
      v-if="!hideMeter && password"
      class="col-span-full flex items-center gap-3"
    >
      <AtomsProgressBar
        v-model="meterProgress[2]"
        line
        :steps="4"
        :step-bg-class="meterProgress[0]"
        class="min-w-0 flex-1 !h-1 rounded-3xl !bg-gray-350"
      />
      <p class="shrink-0 text-right font-bold text-xs text-semantic-surface-on-surface">
        {{ meterProgress[1] }}
      </p>
    </div>
    <div class="md:col-span-11 col-span-10">
      <p class="font-bold text-xs text-semantic-surface-on-surface mb-2">
        {{ label }}
      </p>

      <ul class="list-disc list-inside columns-2 mt-2">
        <li
          v-for="requirement in passwordRequirements"
          :key="requirement.key"
          class="flex items-center font-normal text-xs text-semantic-surface-on-surface"
        >
          <span class="mr-2"><template v-if="requirement.met">
                               <V2IconsCheckCircle
                                 type="outline"
                                 :fill="'#22c55e'"
                                 class="w-3 h-3"
                               />
                             </template>
            <template v-else>•</template></span>
          {{ requirement.value }}
        </li>
      </ul>
    </div>
  </div>
</template>

<script setup>
import memberhubConstants from '~/constants/memberhub.constants'

defineOptions({
  name: 'MoleculesFormPasswordMeter'
})
const props = defineProps({
  schemaObj: {
    type: Object,
    required: true
  },
  forcePoorStatus: {
    type: Boolean,
    default: false
  },
  // Hides the strength progress bar + Poor/Weak/Good/Strong label, leaving only the
  // requirements checklist — used where the design has no strength meter at all.
  hideMeter: {
    type: Boolean,
    default: false
  },
  // The live password value. The requirements checklist always renders regardless, but
  // the progress bar + Poor/Weak/Good/Strong label only appear once this is non-empty —
  // an untouched field shouldn't show "Poor" before the user has typed anything.
  password: {
    type: String,
    default: ''
  },
  label: {
    type: String,
    default: 'Password must contain:'
  },
  dataTestid: {
    type: String,
    default: 'molecules-form-password-meter'
  }
})

// Design-system success green. Strong used bg-primary (purple), so a strong password never
// read as green.
const STRONG_CLASS = 'bg-semantic-states-success-default'

/*
 * Password meter progress / step(s) handler
 * Handles progress bar CSS classes & Step(s) label
 * @returns {(string|number)[]}
 */
const meterProgress = computed(() => {
  let meterStatusClass = 'no-status'
  let meterStatusText = 'Poor'
  let currentMeter = memberhubConstants.PASSWORD_METER.POOR
  if (props.forcePoorStatus) {
    meterStatusClass = 'bg-alert-error-stroke'
    meterStatusText = 'Poor'
    currentMeter = memberhubConstants.PASSWORD_METER.POOR
    return [meterStatusClass, meterStatusText, currentMeter]
  }
  if (props.schemaObj?.error?.errorObj?.passwordMeter) {
    switch (props.schemaObj?.error?.errorObj?.passwordMeter) {
      case memberhubConstants.PASSWORD_METER.POOR:// Poor Password
        meterStatusClass = 'bg-alert-error-stroke'
        meterStatusText = 'Poor'
        currentMeter = memberhubConstants.PASSWORD_METER.POOR
        break
      case memberhubConstants.PASSWORD_METER.WEAK:// Weak Password
        meterStatusClass = 'bg-amber-400'
        meterStatusText = 'Weak'
        currentMeter = memberhubConstants.PASSWORD_METER.WEAK
        break
      case memberhubConstants.PASSWORD_METER.GOOD:// Good Password
        meterStatusClass = 'bg-alert-success-stroke'
        meterStatusText = 'Good'
        currentMeter = memberhubConstants.PASSWORD_METER.GOOD
        break
      case memberhubConstants.PASSWORD_METER.STRONG:// Strong Password
        meterStatusClass = STRONG_CLASS
        meterStatusText = 'Strong'
        currentMeter = memberhubConstants.PASSWORD_METER.STRONG
        break
      default:
        meterStatusClass = props.schemaObj?.error?.errorObj?.passwordMeter >= memberhubConstants.PASSWORD_METER.STRONG ? STRONG_CLASS : 'no-status'
        meterStatusText = props.schemaObj?.error?.errorObj?.passwordMeter >= memberhubConstants.PASSWORD_METER.STRONG ? 'Strong' : 'Poor'
        currentMeter = props.schemaObj?.error?.errorObj?.passwordMeter >= memberhubConstants.PASSWORD_METER.STRONG ? memberhubConstants.PASSWORD_METER.STRONG : memberhubConstants.PASSWORD_METER.POOR
        break
    }
  }
  return [meterStatusClass, meterStatusText, currentMeter]
})

/*
 * Password requirements array with met status
 * Always shows all requirements and indicates which are met
 * @returns {Array}
 */
const passwordRequirements = computed(() => {
  const allRequirements = [
    { key: memberhubConstants.PASSWORD_METER_REQUIRED_KEYS.MIN_LENGTH, value: '8 characters minimum' },
    { key: memberhubConstants.PASSWORD_METER_REQUIRED_KEYS.UPPERCASE, value: 'an uppercase letter' },
    { key: memberhubConstants.PASSWORD_METER_REQUIRED_KEYS.LOWERCASE, value: 'a lowercase letter' },
    { key: memberhubConstants.PASSWORD_METER_REQUIRED_KEYS.SPECIAL_CHAR,value: 'a special character' },
    { key: memberhubConstants.PASSWORD_METER_REQUIRED_KEYS.NUMBER, value: 'a number' }
  ]

  // NOTE: don't default to [] — we need to distinguish "no validation yet"
  const unmet = props.schemaObj?.error?.errorObj?.passwordMeterRequires

  const notValidated = unmet == null // undefined/null → initial state
  const unmetArr = Array.isArray(unmet) ? unmet : [] // when it IS an array

  return allRequirements.map(req => ({
    ...req,
    // ticks only after validation exists AND this rule is NOT unmet
    met: !notValidated && !unmetArr.includes(req.key)
  }))
})
</script>

<style scoped>
</style>